import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Button, Chip, ConfirmDialog, Crumbs, DataTable, PageHead } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import { useDispatch, useSelector } from "react-redux";
import { openNotificationPopup, openSuccessAlert } from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import {
  useDeleteManagedVersionMutation,
  useGetManagedVersionsQuery,
} from "state/businessValidity/businessValidityApiSlice";
import { findBusinessValidityGaps, lisbonToday } from "utils/businessValidity";

import {
  formatVersionDate,
  getVersionEntityMeta,
  versionStatus,
  versionSummary,
} from "../registry";

function VersionHistoryPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { t, i18n } = useTranslation("common");
  const { entityType, entityId } = useParams();
  const community = useSelector(selectCurrentCommunity);
  const meta = getVersionEntityMeta(entityType, t);
  const returnTo = location.state?.returnTo;
  const label = location.state?.label;
  const [pendingDelete, setPendingDelete] = useState(null);

  const { data: versions = [], isFetching } = useGetManagedVersionsQuery(
    { communityId: community.id, entityType, entityId },
    { refetchOnMountOrArgChange: true }
  );

  const rows = useMemo(
    () =>
      versions
        .map((version) => ({
          ...version,
          start_date: version.start_date || version.validity || null,
        }))
        .sort((a, b) => (b.start_date || "").localeCompare(a.start_date || ""))
        .map((version) => ({
          ...version,
          validity: `${formatVersionDate(version.start_date, i18n.resolvedLanguage) || "—"} – ${
            formatVersionDate(version.end_date, i18n.resolvedLanguage) || "…"
          }`,
          summary: versionSummary(entityType, version, t),
          state: versionStatus(version, lisbonToday(), t),
        })),
    [entityType, i18n.resolvedLanguage, t, versions]
  );
  const gaps = useMemo(() => findBusinessValidityGaps(rows), [rows]);
  const [deleteVersion, { isLoading: isDeleting }] = useDeleteManagedVersionMutation();
  const deletesLastResourceRevision = entityType === "resource" && rows.length === 1;
  let deleteDialogBody = "";
  if (pendingDelete) {
    deleteDialogBody = deletesLastResourceRevision
      ? t("versioning.history.delete.lastResourceBody", { validity: pendingDelete.validity })
      : t("versioning.history.delete.revisionBody", { validity: pendingDelete.validity });
  }
  let deleteConfirmLabel = t("versioning.history.delete.confirmRevision");
  if (isDeleting) deleteConfirmLabel = t("versioning.history.delete.deleting");
  else if (deletesLastResourceRevision) {
    deleteConfirmLabel = t("versioning.history.delete.confirmResource");
  }

  if (!meta) return null;

  const editorBase = `/communities/${community.id}/versions/${entityType}/${encodeURIComponent(
    entityId
  )}`;

  const openContract = (row, action) =>
    navigate(
      `/communities/${community.id}/infrastructure/contracts/${action === "new" ? "new" : row.id}`,
      {
        state: {
          meterId: decodeURIComponent(entityId),
          action,
          meterIdentifier: label,
          effectiveAt: row.start_date,
        },
      }
    );

  const onAdd = () => {
    if (entityType === "contract") openContract({}, "new");
    else navigate(`${editorBase}/new`, { state: { returnTo, label } });
  };

  const onEdit = (row) => {
    if (entityType === "contract") openContract(row, "edit");
    else navigate(`${editorBase}/${row.id}/edit`, { state: { returnTo, label } });
  };

  const confirmDelete = async () => {
    if (!pendingDelete || isDeleting) return;
    try {
      await deleteVersion({
        communityId: community.id,
        entityType,
        entityId,
        versionId: pendingDelete.id,
      }).unwrap();
      dispatch(
        openSuccessAlert({
          title: deletesLastResourceRevision
            ? t("versioning.history.delete.resourceDeletedTitle")
            : t("versioning.history.delete.revisionDeletedTitle"),
          description: deletesLastResourceRevision
            ? t("versioning.history.delete.resourceDeletedDescription")
            : t("versioning.history.delete.revisionDeletedDescription", {
                validity: pendingDelete.validity,
              }),
        })
      );
      setPendingDelete(null);
    } catch (error) {
      dispatch(openNotificationPopup({ notification: error, type: "error" }));
    }
  };

  const columns = [
    { key: "validity", label: t("versioning.history.columns.validity") },
    { key: "summary", label: t("versioning.history.columns.configuration") },
    {
      key: "state",
      label: t("versioning.history.columns.state"),
      render: (value) => <Chip tone={value.tone}>{value.label}</Chip>,
    },
    {
      key: "_actions",
      label: "",
      sort: false,
      align: "right",
      render: (value, row) => (
        <div className="inline-flex items-center gap-1">
          <button
            type="button"
            className="rec-icon-btn"
            title={t("versioning.history.editVersionTitle")}
            onClick={(event) => {
              event.stopPropagation();
              onEdit(row);
            }}
          >
            <Icon name="edit" />
          </button>
          <button
            type="button"
            className="rec-icon-btn"
            style={{ color: "var(--danger)" }}
            title={t("versioning.history.deleteVersionTitle")}
            disabled={isDeleting}
            onClick={(event) => {
              event.stopPropagation();
              setPendingDelete(row);
            }}
          >
            <Icon name="delete" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Crumbs
        items={[
          { label: community?.name || "—" },
          { label: label || meta.title },
          { label: t("versioning.history.breadcrumb") },
        ]}
      />
      <PageHead
        title={meta.title}
        subtitle={t("versioning.history.subtitle")}
        actions={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => (returnTo ? navigate(returnTo) : navigate(-1))}>
              <Icon name="arrow_back" />
              {t("versioning.history.back")}
            </Button>
            <Button onClick={onAdd}>
              <Icon name="add" />
              {t("versioning.history.addVersion")}
            </Button>
          </div>
        }
      />
      <div className="rec-card">
        <DataTable
          columns={columns}
          rows={rows}
          search={false}
          footer={false}
          emptyText={
            isFetching
              ? t("versioning.history.loadingVersions")
              : t("versioning.history.noVersions")
          }
          onRow={onEdit}
        />
      </div>
      <div className="rec-card rec-card-pad mt-rec">
        <div className="flex items-start gap-3">
          <div className="rec-kpi-ic tint-amber shrink-0">
            <Icon name="event_busy" />
          </div>
          <div>
            <h3 className="rec-section-title mb-1">{t("versioning.history.coverageGaps.title")}</h3>
            {gaps.length === 0 ? (
              <p className="m-0 text-[13px] text-ink-3">
                {t("versioning.history.coverageGaps.none")}
              </p>
            ) : (
              <>
                <p className="mt-0 mb-2 text-[13px] text-ink-3">
                  {t("versioning.history.coverageGaps.description")}
                </p>
                <ul className="m-0 pl-5 text-[13px] text-ink">
                  {gaps.map((gap) => (
                    <li key={`${gap.start_date}:${gap.end_date}`}>
                      {formatVersionDate(gap.start_date, i18n.resolvedLanguage)} –{" "}
                      {formatVersionDate(gap.end_date, i18n.resolvedLanguage)}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={!!pendingDelete}
        danger
        title={
          deletesLastResourceRevision
            ? t("versioning.history.delete.resourceTitle")
            : t("versioning.history.delete.revisionTitle")
        }
        body={deleteDialogBody}
        confirmLabel={deleteConfirmLabel}
        cancelLabel={t("versioning.history.delete.cancel")}
        onConfirm={confirmDelete}
        onCancel={() => !isDeleting && setPendingDelete(null)}
      />
    </div>
  );
}

export default VersionHistoryPage;
